
import {
  computed, defineComponent, onMounted, PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import { usePrimeVue } from 'primevue/config';
import { UserState } from '@/composables/User';
import type { FlowScheduleConfig, FlowScheduleRule } from '@/types/flow-settings';
import ScheduleRuleEditor from './ScheduleRuleEditor.vue';

let ruleSeq = 0;
const newRuleId = (): string => {
  ruleSeq += 1;
  return `rule-${Date.now().toString(36)}-${ruleSeq}`;
};

export default defineComponent({
  name: 'ScheduleBuilder',

  components: { ScheduleRuleEditor },

  props: {
    modelValue: {
      type: Object as PropType<FlowScheduleConfig>,
      required: true,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t, locale } = useI18n();
    const primevue = usePrimeVue();

    // Localise the PrimeVue Calendar (the app configures it per-usage, not
    // globally). Only French needs overriding; English is PrimeVue's default.
    onMounted(() => {
      if (locale.value === 'fr') {
        primevue.config.locale.dayNamesMin = ['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa'];
        primevue.config.locale.monthNames = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
        primevue.config.locale.monthNamesShort = ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'];
        primevue.config.locale.today = 'Aujourd\'hui';
        primevue.config.locale.clear = 'Effacer';
        primevue.config.locale.firstDayOfWeek = 1;
      }
    });

    // Evaluation timezone = the shop's IANA zone (e.g. "Europe/Paris").
    const shopTimezone = computed(() => UserState.activeShop?.timezone || 'Europe/Paris');

    const rules = computed(() => props.modelValue.rules ?? []);

    const emitRules = (nextRules: FlowScheduleRule[]) => {
      emit('update:modelValue', { ...props.modelValue, rules: nextRules });
    };

    const onRuleUpdate = (index: number, rule: FlowScheduleRule) => {
      const next = [...rules.value];
      next[index] = rule;
      emitRules(next);
    };

    const onRuleRemove = (index: number) => {
      const next = [...rules.value];
      next.splice(index, 1);
      emitRules(next);
    };

    const onAddRule = () => {
      emitRules([...rules.value, { id: newRuleId(), effect: 'include' }]);
    };

    return {
      t,
      shopTimezone,
      rules,
      onRuleUpdate,
      onRuleRemove,
      onAddRule,
    };
  },
});
